import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Coins, X } from "lucide-react";
import { subscribe } from "../../lib/walletEvents";
import useDialogDismiss from "../../hooks/useDialogDismiss";

// Globally-mounted modal that opens whenever the axios interceptor sees a
// 402 with body.error="insufficient_credits". Shows current balance, the
// required cost, and a "Buy credits" CTA that routes to /account/topup.
export default function InsufficientCreditsModal() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const panelRef = useRef(null);
  const close = () => setOpen(false);
  useDialogDismiss({ open, onClose: close, ref: panelRef });

  useEffect(() => {
    return subscribe("insufficient_credits", (payload) => {
      setData(payload);
      setOpen(true);
    });
  }, []);

  if (!open || !data) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-3 sm:p-4"
      role="presentation"
      onClick={(e) => { if (e.target === e.currentTarget) close(); }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Not enough Zencredits"
        className="bg-bg-primary border border-border-default p-5 sm:p-8 w-full max-w-sm sm:max-w-md max-h-[90vh] overflow-y-auto rounded-xl shadow-xl relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={() => setOpen(false)}
          aria-label="Close"
          className="absolute top-3 right-3 text-text-secondary hover:text-text-primary"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-amber-500/15 flex items-center justify-center shrink-0">
            <Coins className="w-5 h-5 text-amber-400" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-text-primary">Not enough Zencredits</h3>
            <p className="text-sm text-text-secondary mt-1">
              {data.message || "Top up to continue using AI features."}
            </p>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
          <div className="bg-bg-secondary/40 rounded-md p-3">
            <p className="text-text-secondary text-xs">Your balance</p>
            <p className="text-text-primary font-semibold text-lg">{data.balance ?? 0} Zencredits</p>
          </div>
          <div className="bg-bg-secondary/40 rounded-md p-3">
            <p className="text-text-secondary text-xs">Required</p>
            <p className="text-text-primary font-semibold text-lg">{data.required ?? 0} Zencredits</p>
          </div>
        </div>

        <div className="mt-6 flex gap-2 justify-end">
          <button
            onClick={() => setOpen(false)}
            className="px-4 py-2 rounded-md text-sm text-text-secondary hover:bg-bg-secondary/40"
          >
            Cancel
          </button>
          <Link
            to={data.action_url || "/account/topup"}
            onClick={() => setOpen(false)}
            className="px-4 py-2 rounded-md text-sm bg-button-primary text-white hover:opacity-90"
          >
            Buy Zencredits
          </Link>
        </div>
      </div>
    </div>
  );
}
